/*
 * THE TWO CONDITIONS MULTI-LIST CHROME DEPENDS ON, in one hook so neither can
 * stand in for the other (Kevin's rule, 2026-09-14; docs/design/multi-list.md
 * rule 1).
 *
 *   granted        — the account holds `multi_list`. Decides whether any
 *                    multi-list chrome exists at all: the verbs on Manage
 *                    fencers, the create flow, the post-create preference step.
 *   lists.length   — how many lists there are. Decides whether a PICKER renders
 *                    anywhere. With one list there is no picker on Manage
 *                    fencers, Active, Upcoming, Past or Manage notifications,
 *                    even for an account holding the flag.
 *
 * THEY ARE INDEPENDENT AND BOTH ARE CHECKED SEPARATELY, which is the point of
 * returning both rather than a single `showChrome` boolean. A granted account
 * with one list can still create, rename and remove lists; the pickers appear
 * the moment a second list exists. Collapsing the two would make "you may have
 * lists" and "you have lists" the same question, and the first control that
 * needed only one of them would get the other for free.
 *
 * THE GRANT IS NOT THE ENFORCEMENT. Every gated endpoint is refused in
 * `sessionMiddleware` whatever this returns (decision 369); the flag is here so
 * the UI does not offer a control that would 403, which is decision 418's rule
 * about controls with no outcome.
 */

import { useCallback, useEffect, useState } from "react";

import { api, list, type Schemas } from "./api";

export type ListRow = Schemas["ListBody"];

/**
 * The feature name, as the server spells it. One constant rather than the
 * string at each call site: a typo in a gate check fails OPEN in the UI --
 * chrome quietly missing for an account that holds the grant -- which is the
 * failure nobody reports because nothing looks broken.
 */
export const FEATURE_MULTI_LIST = "multi_list";

export interface MultiList {
  /** Whether this account may hold more than the one list onboarding gives it. */
  granted: boolean;

  /**
   * The caller's lists, in the server's order: primary first, then by creation.
   * Empty until the read lands, and empty if it fails -- a failed read costs the
   * chrome and nothing else, exactly as it does on the notifications page, since
   * the screen underneath still works against the current list.
   */
  lists: ListRow[];

  /** Re-read the lists, after a create, a rename, a delete or a re-designation. */
  refresh: () => Promise<ListRow[]>;
}

export function useMultiList(features: string[]): MultiList {
  const [lists, setLists] = useState<ListRow[]>([]);

  const refresh = useCallback(async () => {
    const payload = await api.lists();
    const rows = list(payload.lists);
    setLists(rows);
    return rows;
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .lists()
      .then((payload) => {
        if (!cancelled) setLists(list(payload.lists));
      })
      .catch(() => {
        // Deliberately silent. See `lists` above: this read decides whether
        // chrome renders, and there is nothing for a reader to act on.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // READ UNCONDITIONALLY, whether or not the flag is held: the count is the
  // OTHER condition and an ungranted account still has exactly one list, so the
  // read is cheap and the two conditions stay independent. Gating the fetch on
  // the grant would make `lists` mean "lists, if you are allowed any", which is
  // the collapse this hook exists to prevent.
  return { granted: features.includes(FEATURE_MULTI_LIST), lists, refresh };
}
